import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios";
import { useNotifications } from "@via/hooks/useNotifications";
import { ErrorNotification } from "@via/utils/interfaces";
import {
  AddProjectBucketMemberRequestBody,
  CreateProjectBucketRequestBody,
  RemoveProjectBucketMemberRequestBody,
} from "common";

export const useCreateProjectBucket = (): UseMutationResult<
  void,
  ErrorNotification,
  CreateProjectBucketRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<void, ErrorNotification, CreateProjectBucketRequestBody>({
    mutationFn: async (payload: CreateProjectBucketRequestBody) => {
      const res = await axios.post("/api/v1/project-bucket/create", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "project-bucket-created",
        message: "Project Bucket Created",
        details: "Project bucket has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch all requests starts with "/api/project/v1"
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.some(
            key => typeof key === "string" && key.startsWith("/api/project/v1")
          ),
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "project-bucket-creation-failed",
        message: "Project Bucket Creation Failed",
        details:
          error.response?.data?.message ||
          "Project bucket could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

const fetchListProjectBucketMembers = async (
  projectId: number,
  take: number,
  skip: number
) => {
  try {
    const baseUrl = "/api/v1/project-bucket/members/list";
    const res = await axios.post(baseUrl, {
      projectId,
      take,
      skip,
    });
    return res.data;
  } catch (error) {
    console.log(error);
  }
};

export const useListProjectBucketMembers = (
  projectId: number = 0,
  page: number = 0,
  pageSize: number = 10
) => {
  const take = pageSize;
  const skip = page * pageSize;

  return useQuery({
    queryKey: ["/api/v1/project-bucket/members/list", projectId, take, skip],
    queryFn: () => fetchListProjectBucketMembers(projectId, take, skip),
    retry: false,
  });
};

export const useAddProjectBucketMember = (): UseMutationResult<
  void,
  ErrorNotification,
  AddProjectBucketMemberRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    void,
    ErrorNotification,
    AddProjectBucketMemberRequestBody
  >({
    mutationFn: async (payload: AddProjectBucketMemberRequestBody) => {
      const res = await axios.post(
        "/api/v1/project-bucket/members/create",
        payload
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "project-bucket-member-added",
        message: "Project Bucket Member Added",
        details: "Project bucket member has been added",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/project-bucket/members/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "project-bucket-member-failed",
        message: "Project Bucket Member Failed",
        details:
          error.response?.data?.message ||
          "Project bucket could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useRemoveProjectBucketMember = (): UseMutationResult<
  void,
  ErrorNotification,
  RemoveProjectBucketMemberRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    void,
    ErrorNotification,
    RemoveProjectBucketMemberRequestBody
  >({
    mutationFn: async (payload: RemoveProjectBucketMemberRequestBody) => {
      const res = await axios.post(
        "/api/v1/project-bucket/members/remove",
        payload
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "project-bucket-member-removed",
        message: "Project Bucket Member Removed",
        details: "Project bucket member has been removed",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/project-bucket/members/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "project-bucket-member-removal-failed",
        message: "Project Bucket Member Removal Failed",
        details:
          error.response?.data?.message ||
          "Project bucket member could not be removed",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
